import type { FloorDM } from '@Model/FloorModels';
import { normalizeParams } from '@Navigator2/urlUtils';
import { waitForMs } from '@Utils/miscUtils';
import { ObservableProp, useClassStore } from '@Utils/useClassStore';
import { customWebStyle } from '@Utils/webStyles';
import type { NavigationContainerRefWithCurrent } from '@react-navigation/native';
import { StackActions } from '@react-navigation/native';
import { injectable } from 'inversify';
import { action, computed } from 'mobx';
import { createRef } from 'react';

import container from '~ioc/ioc';
import { RouteNames } from '~types/navigation';
import type {
  JourneyParameters,
  NavParamsType,
  RootStackParamList,
} from '~types/navigation';

customWebStyle();

type NavStateType = ReturnType<
  NavigationContainerRefWithCurrent<RootStackParamList>['getState']
>;
@injectable()
export class NavigationStore {
  navigatorRef =
    createRef<NavigationContainerRefWithCurrent<RootStackParamList>>();

  currentParams = new ObservableProp<NavParamsType | undefined>(undefined);
  navigatorReady = new ObservableProp(false);
  navState = new ObservableProp<NavStateType | undefined>(undefined);

  navigation?: any;
  setNavigation = (nav: any) => {
    this.navigation = nav;
  };

  @computed get routeStack() {
    return this.navState.value?.routes ?? [];
  }

  @computed get activeProductId() {
    return this.currentParams.value?.productId;
  }

  private init = () => {
    this.readParamsFromUrl();
    this.navigatorRef.current?.addListener('state', () => {
      this.readParamsFromUrl();
    });
    this.navigatorRef.current?.addListener('options', () => {
      this.readParamsFromUrl();
    });

    this.navState.set(this.navigation?.getState());
    this.navigatorRef.current?.addListener('state', () => {
      this.navState.set(this.navigation?.getState());
    });
  };

  getCurrentRouteName = () =>
    this.navigatorRef.current?.getCurrentRoute()?.name;

  getState = () => this.navigatorRef.current?.getState();

  @action private readParamsFromUrl = () => {
    const stringParams = this.navigatorRef.current?.getCurrentRoute()?.params;
    const params = normalizeParams(stringParams as Record<string, string>);
    this.currentParams.set(params as unknown as NavParamsType);
  };

  @action onReady = () => {
    this.init();
    this.navigatorReady.set(true);
  };
  navToFloor = (floorId: FloorDM['id'], depId?: number) => {
    this.replaceRootFloor(floorId, depId);
    this.navigation?.popToTop();
  };

  navigate = async (...params: any) => {
    this.navigatorRef.current?.navigate(...params);
    // because the navigate action returns immediately
    // and before state/options events are trigger the 'currentParams'
    // do not always reflect the status of the URL
    // allowing to await a bit makes sure that these two are in sync
    // after the navigation attempt
    await waitForMs(250);
  };

  navToProductId = async (productId?: number) => {
    const params = { ...this.currentParams.value, productId } as NavParamsType;

    this.navigate(this.getCurrentRouteName(), params).then();
  };

  pushToDetailView = async (params: JourneyParameters) => {
    this.navigation.push(RouteNames.BigBrowse, params);
  };

  resetNav = async () => {
    // TODO - this will not work in mobile. Will have to make it better
    window.location.href = `/${RouteNames.BigBrowse}`;
  };

  popToRoot = () => {
    this.navigation.popToTop();
  };

  pop = () => {
    this.navigation.pop();
  };

  replaceRootFloor = (floorId: FloorDM['id'], depId?: number) => {
    this.navigation.dispatch({
      ...StackActions.replace(RouteNames.BigBrowse, {
        ...{
          journey: 'floor',
          journeyParam: floorId,
        },
        ...(depId && { depId }),
      }),
      source: this.navigatorRef.current?.getRootState().routes[0].key,
      target: this.navigation.getState().key,
    });
  };
}

export const useNavigationStore = () =>
  useClassStore<NavigationStore>(container.get(NavigationStore));
